import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, School, CreditCard, TrendingUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PrivateSectorSubDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/private-sector-subscription-stats`, { headers: getAuthHeaders() });
      if (res.ok) {
        setStats(await res.json());
      } else {
        toast.error('Failed to load stats');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">Loading private sector stats...</CardContent>
      </Card>
    );
  }

  if (!stats?.active_term) {
    return (
      <Card className="border-amber-200">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">No Active Term</h3>
          <p className="text-sm text-gray-500">Subscription stats are tied to the active academic term. Ask the admin to activate a term.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="private-sector-dashboard">
      {/* Header */}
      <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <School className="h-5 w-5 text-indigo-600" />
            Private Sector Teachers Subscription
          </CardTitle>
          <CardDescription>
            Lesson notes subscription stats for private sector teachers
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <Badge variant="outline" className="bg-indigo-100 text-indigo-700 border-indigo-300">
            {stats.active_term.name} — {stats.active_term.academic_year}
          </Badge>
          <p className="text-xs text-gray-500 mt-1">Stats reset when this term is toggled off</p>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card data-testid="stat-teachers">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-700">{stats.total_private_teachers}</p>
            <p className="text-xs text-gray-500">Teachers / Schools</p>
            <p className="text-xs text-gray-400 mt-0.5">{stats.total_private_schools} school(s)</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-subscribed">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-700">{stats.subscribed_teachers}</p>
            <p className="text-xs text-gray-500">Subscribed Teachers</p>
            <p className="text-xs text-gray-400 mt-0.5">of {stats.total_private_teachers} total</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-price">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-700">{stats.currency} {stats.subscription_price}</p>
            <p className="text-xs text-gray-500">Per Subscription</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-revenue">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-700">{stats.currency} {stats.total_revenue}</p>
            <p className="text-xs text-gray-500">Total Revenue</p>
            <p className="text-xs text-gray-400 mt-0.5">This term</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivateSectorSubDashboard;
